import { ParsedUrlQuery } from 'querystring'

export type Params = { [key: string]: string }
export type FacetParameterInput = {
  name: string
  values: string[]
}

export const BOOLEAN_FACET_NAME = 'showonly'

const FILTER_PARAMS_BLACKLIST = [
  'listType',
  'sortby',
  'pagesize',
  'searchPhrase',
  'page',
  'json',
  'lang',
  'searchCategory',
  'view',
  'ssel',
  'searchwithin',
  'categorypath',
  'categoryPath',
  'path',
]

const TRACKING_PARAMS_BLACKLIST = new RegExp(/^(utm|elq|gad_|gclid|_gl|fbclid|gbraid|msclkid|at_)/)

const ROUTE_PARAMS_BLACKLIST = ['categoryErpIdentifier', 'searchPhrase', 'searchCategory']

/**
 * Splits a facet value string using standardized delimiters.
 *
 * Preferred delimiter: '|'
 * Fallback delimiter: ','
 *
 * @param facetValue - String containing delimited values
 * @returns Array of non-empty decoded values
 */
function parseFacetValues(facetValue: string): string[] {
  if (!facetValue) return []

  // Use '|' as the standard delimiter, ',' for legacy support
  const encodedPipeOperator = '%7C'
  const usePipe =
    !facetValue.includes(',') &&
    (facetValue.includes('|') || facetValue.includes(encodedPipeOperator))

  return facetValue
    .split(usePipe ? '|' : ',')
    .map(v => decodeURIComponent(v.trim()))
    .filter(Boolean)
}

export const stripUnwantedRouteParams = (params: ParsedUrlQuery) => {
  const clonedParams = { ...params }

  ROUTE_PARAMS_BLACKLIST.forEach(routeParam => {
    if (clonedParams[routeParam]) {
      delete clonedParams[routeParam]
    }
  })

  return clonedParams
}

export const parseParams = (params: ParsedUrlQuery) => {
  const parsedParams = Object.entries(stripUnwantedRouteParams(params)).reduce(
    (acc, [key, value]) => {
      // TODO: remove when params are not sent from storwebben anymore
      if (key.toLocaleLowerCase().includes('global.')) {
        return acc
      }

      acc[key] = (Array.isArray(value) ? value[0] : value) || ''
      return acc
    },
    {} as Params
  )

  return parsedParams
}

export const getActiveFilters = (params: Params): FacetParameterInput[] => {
  const facetsWithoutBooleans = Object.keys(params)
    .filter(
      key =>
        !FILTER_PARAMS_BLACKLIST.includes(key) &&
        !TRACKING_PARAMS_BLACKLIST.test(key) &&
        key !== BOOLEAN_FACET_NAME
    )
    .map(name => ({ name, values: [...new Set(parseFacetValues(params[name] || ''))] }))

  const booleanFacetValue: string = params[BOOLEAN_FACET_NAME] || ''

  if (booleanFacetValue.length > 0) {
    const filterValues = parseFacetValues(booleanFacetValue)

    // TODO: This is hopefully a temporary work-around for the lack of `showonly` support in search-query
    const booleanFacet: FacetParameterInput = {
      name: BOOLEAN_FACET_NAME,
      values: filterValues,
    }

    const booleanFacetsAsData: FacetParameterInput[] = filterValues.map(value => ({
      name: value,
      values: ['true'],
    }))

    return [...facetsWithoutBooleans, booleanFacet, ...booleanFacetsAsData]
  }

  return facetsWithoutBooleans
}
